import type { TreeRow } from '../../interfaces/pieces/TreeRow';

type UpdateNodeResult = {
	updated: boolean;
	data: TreeRow[];
};

type FindNodeResult = {
	node: TreeRow | null;
	parent: TreeRow | null;
};

/**
 * Cette classe permet de manipuler les données sans dépendre des symbols
 * ajoutés par rsuite-table, évitant ainsi les problèmes de synchronisation
 */
// biome-ignore lint/complexity/noStaticOnlyClass: Je ne sais pas pourquoi c'est en static
class TreeDataManager {
	private static searchNode(
		nodes: TreeRow[],
		parent: TreeRow | null,
		rowKey: string,
	): FindNodeResult | null {
		let result: FindNodeResult | null = null;

		nodes.some((node) => {
			if (node.rowKey === rowKey) {
				result = { node, parent };
				return true;
			}

			if (node.children?.length) {
				const childResult = TreeDataManager.searchNode(
					node.children,
					node,
					rowKey,
				);
				if (childResult) {
					result = childResult;
					return true;
				}
			}

			return false;
		});

		return result;
	}

	private static updateNodeRecursive(
		nodes: TreeRow[],
		rowKey: string,
		updater: (node: TreeRow) => TreeRow,
	): { nodes: TreeRow[]; updated: boolean } {
		let updated = false;

		const newNodes = nodes.map((node) => {
			if (node.rowKey === rowKey) {
				updated = true;
				return updater(node);
			}

			if (node.children?.length) {
				const childResult = TreeDataManager.updateNodeRecursive(
					node.children,
					rowKey,
					updater,
				);
				if (childResult.updated) {
					updated = true;
					return { ...node, children: childResult.nodes };
				}
			}

			return node;
		});

		return { nodes: newNodes, updated };
	}

	private static deleteNodeRecursive(
		nodes: TreeRow[],
		rowKey: string,
	): TreeRow[] {
		return nodes.reduce<TreeRow[]>((acc, node) => {
			if (node.rowKey === rowKey) return acc;

			if (node.children?.length) {
				const updatedChildren = TreeDataManager.deleteNodeRecursive(
					node.children,
					rowKey,
				);
				if (updatedChildren !== node.children) {
					acc.push({ ...node, children: updatedChildren });
					return acc;
				}
			}

			acc.push(node);
			return acc;
		}, []);
	}

	/**
	 * Trouve un noeud dans l'arbre par son rowKey.
	 * Retourne le noeud et son parent direct.
	 */
	static findNode(rows: TreeRow[], rowKey: string): FindNodeResult {
		return (
			TreeDataManager.searchNode(rows, null, rowKey) ?? {
				node: null,
				parent: null,
			}
		);
	}

	/**
	 * Met à jour un noeud dans l'arbre par son rowKey.
	 * Retourne une nouvelle copie de l'arbre avec le noeud mis à jour.
	 */
	static updateNode(
		rows: TreeRow[],
		rowKey: string,
		updater: (node: TreeRow) => TreeRow,
	): UpdateNodeResult {
		const { nodes, updated } = TreeDataManager.updateNodeRecursive(
			rows,
			rowKey,
			updater,
		);
		return { data: nodes, updated };
	}

	/**
	 * Met à jour les children d'un noeud identifié par son rowKey.
	 */
	static updateNodeChildren(
		rows: TreeRow[],
		rowKey: string,
		children: TreeRow[],
	): TreeRow[] {
		const { data } = TreeDataManager.updateNode(rows, rowKey, (node) => ({
			...node,
			children,
		}));
		return data;
	}

	/**
	 * Supprime un noeud de l'arbre par son rowKey.
	 * Retourne une nouvelle copie de l'arbre sans le noeud.
	 */
	static deleteNode(rows: TreeRow[], rowKey: string): TreeRow[] {
		return TreeDataManager.deleteNodeRecursive(rows, rowKey);
	}

	/**
	 * Trouve le parent d'un noeud dans l'arbre original (data).
	 * Utile pour éviter d'utiliser les symbols de filterData.
	 */
	static getParentFromData(
		data: TreeRow[],
		childRowKey: string,
	): TreeRow | null {
		const { parent } = TreeDataManager.findNode(data, childRowKey);
		return parent;
	}
}

export default TreeDataManager;
